import { isDefined, makeValuesPredicate } from '@sgme/fp';

import type { FalconOrder, NightjarOrder, TwapOrder } from '@/store/orders/algoOrders/state.ts';
import type { CallOrder } from '@/store/orders/call/state.ts';
import type { FixingOrder } from '@/store/orders/fixing/state.ts';
import type { StopLossOrder } from '@/store/orders/stopLoss/state.ts';
import type { TakeProfitOrder } from '@/store/orders/takeProfitOrder/state.ts';

export type OrderById = Record<string, Order>;

export type Order = TakeProfitOrder | StopLossOrder | FixingOrder | CallOrder | TwapOrder | FalconOrder | NightjarOrder;

export const limitOrdersTypes = ['TakeProfitOrder', 'StopLossOrder', 'CallOrder', 'FixingOrder'] as const;

export const algoOrderTypes = ['Twap+Order', 'NightjarOrder', 'FalconOrder'] as const;

export const ALL_ORDER_TYPES = [...limitOrdersTypes, ...algoOrderTypes] as const;

export type OrderType = (typeof ALL_ORDER_TYPES)[number];

export type AlgoOrderType = (typeof algoOrderTypes)[number];
export const isAlgoOrder = makeValuesPredicate(algoOrderTypes);

export type LegSide = 'Buy' | 'Sell';
export type Side = LegSide | 'Both';

export const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export type SubmitOrderResponse =
  | {
      type: 'success';
      orderId: string;
    }
  | {
      type: 'failure';
      errors: ValidationResponseDetailContent[];
    };

export type ValidationResponseDetailContent = {
  field: string;
  errorDescription: string;
  errorCode: number;
};

export type ValidateOrderResponse = {
  message: string; // "Validation succeeded",
  validationResponseDetailContent: ValidationResponseDetailContent[];
  isReadyToSubmit: boolean;
  expiryDay: string | null;
  defaultPrice: number | undefined;
  maturityDay: string; // "2024-10-28T08:19:15.0090021Z",
  fixingTypes: string[]; // ["BFIX", "WMR", "OTHER", "KFTC"],
  isCcyForcedBidAsk: boolean;
  isStandardGroup: boolean;
  fixingTimesOfSelectedType: string[]; // ["16:00 London Time", "10:00 NewYork time"],
  sourceNameOfSelectedType: string[]; // ["KFTC18", "KFTC19"],
  canModifyMarginInBps: boolean;
  marginInBps: number | null;
  ndfFixingDate: string;
  ndfFixingSource: string;
  limitPrice: number | null;
  customerPrice: number | null;
  margin: number | null;
  backendTime: number;
};

export function isError400WithPayload(response: any): response is ValidateOrderResponse {
  return (
    response &&
    'validationResponseDetailContent' in response &&
    response.isReadyToSubmit !== undefined &&
    Array.isArray(response.validationResponseDetailContent)
  );
}

export function isSubmitOrderResponse(response: unknown): response is SubmitOrderResponse {
  return isDefined(response) && typeof response === 'object' && 'errors' in (response as object);
}
